
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { BaseLayout } from '@/components/templates';
import { withUrql } from '@/config/graphql';
import slice from 'lodash/slice';
import { GtmEvent, GtmEventCategory, GtmGameData, pushDataLayer } from '@/utils/gtm';
import { Box, Button, Center, Container, HStack, Link } from '@chakra-ui/react';
import { contentfulClient } from '@cms/client';
import { BaseGameFieldsFragment, GamesNavigationDocument, GamesNavigationQuery, GamesOverviewPageDocument, GamesOverviewPageQuery, } from '@cms/graphql/gamesOverviewPage.generated';
import { PageGamesOverview } from '@cms/graphql/types.generated';
import { IconName, TextualHero, Loader } from '@meritcircle/ui/components';
import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { canonicalUrl as getCanonicalUrl } from '@/utils/seo';
import { SeoFieldsFragment } from '@cms/graphql/components/componentSeo.generated';
import { Seo } from '@/components/utils/seo/seo-component';
import { URLS } from '@/config/urls';
import GameOverviewHero from '@/components/features/games/game-overview-hero/game-overview-hero';
import NextLink from 'next/link';
import { isExternalUrl } from '@meritcircle/ui';
import { BLOCKS, Document } from '@contentful/rich-text-types';
import { parseGamesToCarouselSlides } from '@/components/features/games/game-carousel/game-carousel.helpers';
import { GameCarousel } from '@/components/features/games/game-carousel/game-carousel';
import { GameTag } from '@/client/graphql/types.generated';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useState } from 'react';
import { GamePages, GamesNavigation, getGamesUrl, getNavigation } from '@/utils/games';
import { GameNav } from '@/components/features/games/game-nav/game-nav';
import { GenreCarousel } from '@/components/features/games/genre-carousel/genre-carousel';
import { getOpenGraphProps } from '@/components/utils/seo/get-open-graph-props';
type PageProps = InferGetServerSidePropsType<typeof getServerSideProps>;
interface TrackingGame extends BaseGameFieldsFragment {
    list: string;
    position?: number;
}
const NUMBER_OF_SECTION_TO_LOAD = 5;
const getTrackGame = (game: TrackingGame): GtmGameData => ({
    name: game.title,
    id: game.id,
    price: 1,
    tokens: game.tokens?.join(', ') || undefined,
    category: game.categories?.join(', ') || undefined,
    platform: game.platforms?.join(', ') || undefined,
    list: game.list,
    position: game.position
});
const handleCardClick = (game: TrackingGame) => pushDataLayer({
    event: GtmEvent.gameClick,
    games: [getTrackGame(game)]
});
const handleCardImpression = (game: TrackingGame) => pushDataLayer({
    event: GtmEvent.gameImpression,
    games: [getTrackGame(game)]
});
const Page: NextPage<PageProps> = ({ metaDescription, metaTitle, ogImage, noFollow, noIndex, keywords, title, description, linkedSeoTags, navigation, heroSection, pageBuilder, gamesCollectionLength, lastUpdate, }) => {
    const { t } = useTranslation('games');
    const router = useRouter();
    const [numberOfVisibleSections, setNumberOfVisibleSections] = useState(NUMBER_OF_SECTION_TO_LOAD);
    const pageSections = pageBuilder.rows || [];
    const handlePlayButtonClick = (eventLabel: string, eventAction: 'installation guide' | 'launch' | 'download and play' | 'app store' | 'google play') => pushDataLayer({
        event: GtmEvent.playGame,
        eventCategory: GtmEventCategory.game,
        eventAction,
        eventLabel
    });
    const shouldRenderDirectPlayButton = heroSection?.game.isGameLive &&
        !heroSection?.game.hasInstallationGuide &&
        heroSection?.game.linkedLaunchedButtonsComponent?.directPlayLink;
    const subtitleDocument: Document = {
        nodeType: BLOCKS.DOCUMENT,
        data: {},
        content: [
            {
                nodeType: BLOCKS.HEADING_6,
                data: {},
                content: [
                    {
                        nodeType: 'text',
                        value: description,
                        data: {},
                        marks: [{ type: '' }]
                    },
                ]
            },
        ]
    };
    const fetchMoreRows = async () => {
        setNumberOfVisibleSections(numberOfVisibleSections + NUMBER_OF_SECTION_TO_LOAD);
    };
    const canonicalUrl = getCanonicalUrl(router);
    const visiblePageSections = slice(pageSections, 0, numberOfVisibleSections);
    return (<BaseLayout>
      <Seo title={linkedSeoTags?.title || metaTitle} description={linkedSeoTags?.description || metaDescription} nofollow={linkedSeoTags?.noFollow || noFollow} noindex={linkedSeoTags?.noIndex || noIndex} keywords={linkedSeoTags?.keywords || keywords} lastUpdate={lastUpdate} canonical={canonicalUrl} openGraph={getOpenGraphProps({
            title: linkedSeoTags?.title || metaTitle,
            description: linkedSeoTags?.description || metaDescription,
            url: canonicalUrl,
            image: ogImage
        })}/>
      <TextualHero title={title} subtitle={subtitleDocument} pb="4xl"/>

      <Container pb="space.24">
        <GameNav links={navigation}/>
      </Container>

      <InfiniteScroll dataLength={numberOfVisibleSections} next={fetchMoreRows} hasMore={Boolean(numberOfVisibleSections < gamesCollectionLength)} loader={<Center>
            <Loader />
          </Center>}>
        {visiblePageSections.filter(Boolean).map((row, index) => (<Box as="section" pb={{ base: '2xl', md: '4xl' }} key={index}>
            {row?.__typename === 'ComponentGameCarousel' && (<>
                {row?.type === 'game' ? (<GameCarousel title={row.title} description={row.subtitle} textLink={row.linkText
                        ? {
                            children: row.linkText,
                            href: `${URLS.GAMES}/${row.slug}`,
                            bg: 'lightPurple',
                            bgClip: 'text',
                            rightIcon: {
                                icon: IconName.arrowRightFill
                            }
                        }
                        : undefined} onClick={(index: number) => handleCardClick({
                        list: row.title,
                        position: index,
                        ...row.gamesCollection.items[index]
                    })} onInView={(index: number) => handleCardImpression({
                        list: row.title,
                        position: index,
                        ...row.gamesCollection.items[index]
                    })} slides={parseGamesToCarouselSlides(row.gamesCollection.items, t)}/>) : (<GenreCarousel title={row.title} description={row.subtitle} textLink={row.linkText
                        ? {
                            children: row.linkText,
                            href: `${URLS.GAMES}/${row.slug}`,
                            bgClip: 'text',
                            rightIcon: {
                                icon: IconName.arrowRightFill
                            }
                        }
                        : undefined} slides={row.gamesCollection.items}/>)}
              </>)}

            {row?.__typename === 'ComponentGameOverviewHero' && (<GameOverviewHero heroImageUrl={`${row?.game?.mainImage.url}?w=3800`} summary={row?.game?.summary} badges={row.game?.gameTagsCollection.items.map((item: GameTag) => item.name)} tokens={row?.game?.tokens} title={row?.game?.title} titleLevel="h2" gameSlug={row?.game.urlSlug}>
                <Box slot="buttons">
                  <HStack mt={'2rem'}>
                    {shouldRenderDirectPlayButton && (<NextLink href={row?.game?.linkedLaunchedButtonsComponent?.directPlayLink || ''} passHref legacyBehavior>
                        <Button as={Link} isExternal={isExternalUrl(row?.game?.linkedLaunchedButtonsComponent?.directPlayLink)} variant="primary" onClick={() => handlePlayButtonClick(row?.game.title, 'launch')}>
                          {t('buttons.play-game')}
                        </Button>
                      </NextLink>)}
                    <NextLink href={getGamesUrl(row?.game.urlSlug)} passHref legacyBehavior>
                      <Button as={Link} variant="secondary">
                        {t('buttons.more-info')}
                      </Button>
                    </NextLink>
                  </HStack>
                </Box>
              </GameOverviewHero>)}
          </Box>))}
      </InfiniteScroll>
    </BaseLayout>);
};
const getServerSideProps: GetServerSideProps<Pick<PageGamesOverview, 'title' | 'description' | 'metaTitle' | 'metaDescription' | 'ogImage' | 'noFollow' | 'noIndex' | 'keywords'> & {
    heroSection: {
        title: PageGamesOverview['heroSection']['title'];
        game: BaseGameFieldsFragment;
    } | null;
    pageBuilder: {
        title: PageGamesOverview['pageBuilder']['title'];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rows: PageGamesOverview['pageBuilder']['rowsCollection']['items'] | Array<any>;
    } | null;
    navigation: GamesNavigation;
    linkedSeoTags: SeoFieldsFragment;
    gamesCollectionLength: number;
    lastUpdate: string;
}, {
    slug: string;
}> = async () => {
    console.log('trying to get game overview pages');
    const overviewPage = await contentfulClient
        .query<GamesOverviewPageQuery>(GamesOverviewPageDocument, {
        pageType: GamePages.OVERVIEW
    })
        .toPromise();
    const pageContent = overviewPage.data?.pageGamesOverviewCollection?.items?.[0];
    const gamesCollectionLength = pageContent?.pageBuilder?.rowsCollection?.total || 0;
    if (!pageContent) {
        console.warn('👻 Games overview page missing');
        return {
            notFound: true
        };
    }
    const { description, title, metaTitle, metaDescription, linkedSeoTags } = pageContent;
    const { data: navigationData } = await contentfulClient
        .query<GamesNavigationQuery>(GamesNavigationDocument)
        .toPromise();
    const navigation = getNavigation(navigationData?.pageGamesOverviewCollection.items);
    const heroSection = pageContent.heroSection
        ? {
            title: pageContent.heroSection.title,
            game: pageContent.heroSection.game
        }
        : null;
    const pageBuilder = pageContent.pageBuilder
        ? {
            title: pageContent.pageBuilder.title,
            rows: pageContent.pageBuilder.rowsCollection.items,
            total: pageContent.pageBuilder.rowsCollection.total
        }
        : null;
    console.log('✨ Fetched game overview page from contentful', new Date());
    return {
        props: {
            description,
            title,
            metaDescription,
            metaTitle,
            linkedSeoTags,
            heroSection,
            navigation,
            pageBuilder,
            gamesCollectionLength,
            lastUpdate: `${new Date()}`
        }
    };
};
export default withUrql(Page);

    async function __Next_Translate__getServerSideProps__1973b3b2a3d__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/games/index',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1973b3b2a3d__ as getServerSideProps }
  